import { displayAddress } from '@/webapp/siteAddress';
import { SITE_STATUS_LABEL } from '@/utils/websiteSheet';
import { Chip } from '../../ui';
import type { SiteCheck } from '../../state/siteStatus';
import { funnelStep } from '../../analytics/funnel';

/**
 * The website, in the editor's top bar: "Open your site ↗", or why there is nothing to open.
 *
 * "I should not even see the preview — all I need is the link to a new tab and the chat" (the
 * owner, 2026-09-29). So this link is the one way to see the site from the editor, and it is the
 * SERVED page — never the generated source opened from a blob: or data: URL, which would run it
 * on the builder's own origin, where it could read the builder's tokens.
 *
 * The address is the server's for a page it serves, and the record's while it has not answered.
 * A page the server says is down is not a link: it would open "This site isn't available", which
 * is what the sentence already says — with the way to turn it back on.
 */
export default function SiteLine({
  webUrl,
  site,
  onOpenSettings,
}: {
  webUrl?: string;
  site: SiteCheck;
  onOpenSettings: () => void;
}) {
  const known = site.kind === 'known' ? site : null;
  const offline = known?.status === 'offline';
  const url = offline ? undefined : (known?.siteUrl ?? webUrl);

  if (url && /^https?:\/\//.test(url)) {
    return (
      <a
        className="btn btn-primary clay-primary pressable editor-open-site"
        href={url}
        target="_blank"
        rel="noreferrer"
        title={displayAddress(url)}
        // The funnel's last step: the site they built, opened (analytics/funnel.ts).
        onClick={() => funnelStep('site_opened')}
      >
        Open your site <span aria-hidden="true">↗</span>
      </a>
    );
  }
  return (
    <p className="type-caption editor-offline">
      <span>
        {offline
          ? 'Your site is offline — turn it on in Settings.'
          : 'Your site isn’t online yet — publish it in Settings.'}
      </span>{' '}
      <button type="button" className="link-button" onClick={onOpenSettings}>Open Settings</button>
    </p>
  );
}

/** Whether the page is up, in the phone's word — shown only once the server has said. */
export function SiteStatusChip({ site }: { site: SiteCheck }) {
  if (site.kind !== 'known') return null;
  return (
    <Chip
      label={SITE_STATUS_LABEL[site.status]}
      tone={site.status === 'live' ? 'success' : site.status === 'password' ? 'primary' : 'neutral'}
    />
  );
}
